import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, Quote, TriangleAlert, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { searchPropositions } from '@/services/anatel';
import type { PropositionFilters, PropositionHit } from '@/services/types';

const PAGE_SIZE = 25;

/**
 * Search across what ANATEL decisions SAID, rather than across the decisions.
 *
 * The rest of the platform answers "what did this decision hold?" from inside a
 * case. This answers "which decisions have held X?", which is the question a
 * lawyer actually arrives with.
 *
 * The screen carries one obligation the other list screens do not. Everywhere
 * else an empty result means "nothing matched your filters"; here it reads as
 * "no ANATEL decision has ever held this", which is a claim about the corpus. So
 * three states that all render as zero rows are kept visibly distinct: the query
 * had no searchable term, nothing matched, and the reasoning layer reaches only
 * part of the corpus so a miss may be a coverage gap rather than an absence.
 */
export function AnatelPropositionsPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [draft, setDraft] = useState('');
  const [filters, setFilters] = useState<PropositionFilters>({
    query: null,
    limit: PAGE_SIZE,
    offset: 0,
  });

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['anatel', 'propositions', filters],
    queryFn: () => searchPropositions(filters),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const offset = filters.offset ?? 0;

  /** Any filter change resets to page 1: keeping the offset would land the
      reader on an empty page of a narrower result set. */
  const update = (next: Partial<PropositionFilters>) =>
    setFilters((current) => ({ ...current, ...next, offset: 0 }));

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:propositions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:propositions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:propositions.explainer" />

      <FieldGuide i18nKey="anatel:propositions.guide" level={2} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/anatel/export/propositions" body={filters} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          // Submit-driven, never search-as-you-type: each query is a ~1.2s scan
          // of 68,843 rows because the source has no index this app may add.
          update({ query: draft.trim() || null, issue: null });
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="proposition-query" className="text-xs font-medium text-foreground">
            {t('anatel:propositions.search.label')}
          </label>
          <input
            id="proposition-query"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('anatel:propositions.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
          <p className="text-xs text-muted-foreground">{t('anatel:propositions.search.hint')}</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="proposition-polarity" className="block text-xs font-medium text-foreground">
            {t('anatel:propositions.filters.polarity')}
          </label>
          <select
            id="proposition-polarity"
            value={filters.polarity ?? ''}
            onChange={(event) => update({ polarity: event.target.value || null })}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('anatel:propositions.filters.polarityAny')}</option>
            <option value="affirms">{t('anatel:propositions.polarity.affirms')}</option>
            <option value="denies">{t('anatel:propositions.polarity.denies')}</option>
          </select>
        </div>

        <label className="flex items-center gap-2 py-1.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={filters.verified_only ?? false}
            onChange={(event) => update({ verified_only: event.target.checked })}
            className="h-4 w-4 rounded border"
          />
          {t('anatel:propositions.filters.verifiedOnly')}
        </label>

        <label className="flex items-center gap-2 py-1.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={filters.final_only ?? false}
            onChange={(event) => update({ final_only: event.target.checked })}
            className="h-4 w-4 rounded border"
          />
          {t('anatel:propositions.filters.finalOnly')}
        </label>

        {/* `bg-foreground text-background`, not `bg-primary text-primary-foreground`.
            The brand orange carries white text at 3.83:1 (light), 3.25:1 (dark)
            and 4.50:1 (clean) -- below or exactly at the 4.5:1 AA floor, which
            the axe check in the smoke suite fails on. Inverting the theme's own
            text and background pair is emphatic and cannot fail, whatever the
            active theme. See the note in Known Pitfalls: the token itself is
            still wrong for the components that already use it. */}
        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('anatel:propositions.search.submit')}
        </button>
      </form>

      {filters.issue ? (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
          <span className="text-muted-foreground">{t('anatel:propositions.issueFilter')}</span>
          <span className="font-medium text-foreground">{filters.issue}</span>
          <button
            type="button"
            onClick={() => update({ issue: null })}
            className="ml-auto inline-flex items-center gap-1 rounded border px-2 py-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
            {t('anatel:propositions.clearIssue')}
          </button>
        </div>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : (
        <div className="space-y-3">
          <Answer answer={data.search} help={t('anatel:propositions.help')}>
            {(summary) => (
              <div className="rounded-lg border bg-card p-3">
                <p className="text-sm text-foreground" aria-live="polite">
                  {t('anatel:propositions.summary', {
                    total: number.format(summary.total),
                    decisions: number.format(summary.decisions),
                  })}
                  {isFetching ? ` · ${t('common:state.loading')}` : ''}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('anatel:propositions.summaryDetail', {
                    verified: number.format(summary.verified),
                    unverified: number.format(summary.unverified),
                    denies: number.format(summary.denies),
                    issues: number.format(summary.issues),
                  })}
                </p>
              </div>
            )}
          </Answer>

          <PropositionResults
            items={data.items}
            total={data.search.value?.total ?? 0}
            noSearchableTerms={data.search.value?.no_searchable_terms ?? false}
            offset={offset}
            onIssueClick={(issue) => update({ issue, query: null })}
            onPage={(next) => setFilters({ ...filters, offset: next })}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Three states that all render as zero rows, kept visibly apart.
 *
 * `no_searchable_terms` means the search never ran; an empty `items` means
 * nothing matched within the extracted part of the corpus. Collapsing them into
 * one "no results" would let a mistyped query read as a statement about what
 * ANATEL has never held.
 */
function PropositionResults({
  items,
  total,
  noSearchableTerms,
  offset,
  onIssueClick,
  onPage,
}: {
  items: PropositionHit[];
  total: number;
  noSearchableTerms: boolean;
  offset: number;
  onIssueClick: (issue: string) => void;
  onPage: (offset: number) => void;
}) {
  const { t } = useTranslation(['anatel', 'common']);

  if (noSearchableTerms) {
    return (
      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-foreground">
        {t('anatel:propositions.noSearchableTerms')}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        {t('anatel:propositions.noMatches')}
      </p>
    );
  }

  return (
    <>
      <ul className="space-y-3">
        {items.map((hit, index) => (
          <PropositionCard
            key={`${hit.process_key}-${hit.decision_number ?? ''}-${index}`}
            hit={hit}
            onIssueClick={onIssueClick}
          />
        ))}
      </ul>

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onPage(Math.max(offset - PAGE_SIZE, 0))}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.previous')}
        </button>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onPage(offset + PAGE_SIZE)}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('anatel:decisions.next')}
        </button>
      </div>
    </>
  );
}

function PropositionCard({
  hit,
  onIssueClick,
}: {
  hit: PropositionHit;
  onIssueClick: (issue: string) => void;
}) {
  const { t } = useTranslation(['anatel', 'common']);
  const denies = hit.polarity === 'denies';

  return (
    <li className="space-y-2 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={
            denies
              ? 'rounded border border-red-500/40 bg-red-500/10 px-2 py-0.5 font-medium text-red-700 dark:text-red-300'
              : 'rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300'
          }
        >
          {t(`anatel:propositions.polarity.${hit.polarity}`, { defaultValue: hit.polarity })}
        </span>

        {/* Verification is stated either way. A silent absence of the badge
            would leave an unverified span looking like a checked citation. */}
        {hit.evidence_verified ? (
          <span className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-muted-foreground">
            <Check className="h-3 w-3" aria-hidden />
            {t('anatel:propositions.verified')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-300">
            <TriangleAlert className="h-3 w-3" aria-hidden />
            {t('anatel:propositions.unverified')}
          </span>
        )}

        {!hit.is_final_observed ? (
          <span className="rounded border px-2 py-0.5 text-muted-foreground">
            {t('anatel:propositions.notFinal')}
          </span>
        ) : null}
      </div>

      <p className="text-sm text-foreground">{hit.proposition_text}</p>

      {hit.legal_basis ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">{t('anatel:propositions.legalBasis')}:</span>{' '}
          {hit.legal_basis}
        </p>
      ) : null}

      {/* The evidence span is the reason a proposition can be trusted at all, so
          it is shown rather than hidden behind a disclosure. */}
      <blockquote className="flex gap-2 border-l-2 border-muted pl-3 text-xs italic text-muted-foreground">
        <Quote className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        <span>{hit.evidence_span}</span>
      </blockquote>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2 text-xs text-muted-foreground">
        <Link
          to={`/anatel/p/${hit.process_key}`}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {hit.process_number}
        </Link>
        {hit.reference_date ? <span className="tabular-nums">{hit.reference_date}</span> : null}
        {hit.entity_name ? <span className="max-w-xs truncate">{hit.entity_name}</span> : null}
        {hit.outcome_codes.length > 0 ? (
          <span>
            {hit.outcome_codes
              .map((code) => t(`anatel:overview.outcomes.${code}`, { defaultValue: code }))
              .join(' · ')}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => onIssueClick(hit.issue)}
          className="ml-auto max-w-xs truncate rounded border px-2 py-0.5 text-left hover:text-foreground"
          title={hit.issue}
        >
          {hit.issue}
        </button>
      </div>
    </li>
  );
}
